import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  FaSearch,
  FaCalendar,
  FaMapMarkerAlt,
  FaExternalLinkAlt,
  FaEnvelope,
  FaGraduationCap,
  FaAward,
  FaCheckCircle,
} from "react-icons/fa";
import { apiCall } from "../config/postgres";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ContactPopup from "../components/ContactPopup";
import ParallaxHeroSection from "../components/ParallaxHeroSection";
import SEO from "../components/SEO";

const ScholarshipCard = ({ scholarship: s, index, t, onContactClick }) => {
  const [expanded, setExpanded] = useState(false);
  const expired = s.deadline && new Date(s.deadline) < new Date();

  return (
    <motion.div
      className="bg-white rounded-lg shadow-lg overflow-hidden h-full flex flex-col"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ y: -10 }}
    >
      <div className="relative">
        {s.image ? (
          <img src={s.image} alt={s.title} className="w-full h-48 object-cover" />
        ) : (
          <div className="w-full h-48 bg-safir-dark flex items-center justify-center">
            <FaGraduationCap className="text-white text-5xl opacity-60" />
          </div>
        )}
        {s.provider && (
          <div className="absolute top-0 right-0 bg-safir-primary text-white px-3 py-1 m-2 rounded-full text-sm font-semibold">
            {s.provider}
          </div>
        )}
        {expired && (
          <div className="absolute top-0 left-0 bg-red-600 text-white px-3 py-1 m-2 rounded-full text-sm font-semibold">
            {t("scholarships.page.expired", "Expired")}
          </div>
        )}
      </div>

      <div className="p-6 flex-grow">
        <h3 className="text-xl font-bold text-safir-dark mb-2">{s.title}</h3>
        <p className="text-gray-600 mb-4">{s.description}</p>

        <div className="flex flex-wrap gap-4 text-gray-500 text-sm mb-4">
          {s.deadline && (
            <div className={`flex items-center ${expired ? "text-red-500" : ""}`}>
              <FaCalendar className="mr-2 text-safir-primary" />
              <span>
                {t("scholarships.page.deadline", "Deadline")}: {new Date(s.deadline).toLocaleDateString()}
              </span>
            </div>
          )}
          {s.location && (
            <div className="flex items-center">
              <FaMapMarkerAlt className="mr-2 text-safir-primary" />
              <span>{s.location}</span>
            </div>
          )}
          {s.benefits && (
            <div className="flex items-center">
              <FaAward className="mr-2 text-safir-primary" />
              <span>{s.benefits.split("\n")[0]}</span>
            </div>
          )}
        </div>

        {(s.eligibility || s.benefits) && (
          <div className="border-t pt-3">
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              className="text-safir-primary font-medium text-sm hover:underline"
            >
              {expanded
                ? t("scholarships.page.showLess", "Show less")
                : t("scholarships.page.showMore", "Eligibility & benefits")}
            </button>
            {expanded && (
              <div className="mt-3 text-sm text-gray-600 space-y-3">
                {s.eligibility && (
                  <div>
                    <p className="font-semibold text-safir-dark mb-1">{t("scholarships.page.eligibility", "Eligibility")}</p>
                    <p className="whitespace-pre-line">{s.eligibility}</p>
                  </div>
                )}
                {s.benefits && (
                  <div>
                    <p className="font-semibold text-safir-dark mb-1">{t("scholarships.page.benefits", "Award / Benefits")}</p>
                    <p className="whitespace-pre-line">{s.benefits}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="px-6 pb-6">
        {s.application_url ? (
          <motion.a
            href={s.application_url}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full bg-safir-primary hover:bg-safir-secondary text-white font-semibold py-2 px-4 rounded-md transition-colors duration-300 flex items-center justify-center"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
          >
            {t("scholarships.page.apply", "Apply Now")}
            <FaExternalLinkAlt className="ml-2 text-sm" />
          </motion.a>
        ) : (
          <motion.button
            className="w-full bg-safir-primary hover:bg-safir-secondary text-white font-semibold py-2 px-4 rounded-md transition-colors duration-300 flex items-center justify-center"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
            onClick={onContactClick}
          >
            <FaEnvelope className="mr-2" />
            {t("scholarships.page.contactUs", "Contact Us to Apply")}
          </motion.button>
        )}
        {expired && (
          <p className="text-xs text-red-500 mt-2 flex items-center justify-center">
            <FaCheckCircle className="mr-1" />
            {t("scholarships.page.expiredNote", "The deadline for this opportunity has passed.")}
          </p>
        )}
      </div>
    </motion.div>
  );
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const EventsPage = () => {
  const { t } = useTranslation();
  const [scholarships, setScholarships] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [showContactPopup, setShowContactPopup] = useState(false);

  useEffect(() => {
    apiCall("/api/scholarships")
      .then((data) => setScholarships(data || []))
      .catch(() => setError(t("scholarships.page.error", "Unable to load scholarships.")))
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = scholarships.filter((s) =>
    [s.title, s.provider, s.description, s.eligibility, s.location, s.benefits]
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  return (
    <div className="bg-gray-50">
      <SEO
        title={t("scholarships.page.title", "Scholarships")}
        description={t("scholarships.page.description", "Discover scholarship opportunities from Safir Foundation.")}
        canonical="/scholarships"
      />
      <Navbar />

      <ParallaxHeroSection
        backgroundKey="courses"
        title={t("scholarships.page.title", "Scholarships")}
        subtitle={t("scholarships.page.subtitle", "Explore opportunities to support your education.")}
      >
        <motion.div
          className="w-24 h-1 bg-safir-primary mx-auto mt-4"
          initial={{ width: 0 }}
          animate={{ width: 96 }}
          transition={{ duration: 0.8 }}
        ></motion.div>
      </ParallaxHeroSection>

      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-md p-6 mb-8 -mt-16">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch className="text-gray-400" />
            </div>
            <input
              type="text"
              placeholder={t("scholarships.page.searchPlaceholder", "Search scholarships...")}
              className="pl-10 w-full p-3 border border-gray-300 rounded-md focus:ring-safir-primary focus:border-safir-primary"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-safir-primary mx-auto"></div>
            <p className="mt-4 text-gray-600">{t("scholarships.page.loading", "Loading scholarships...")}</p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-xl text-red-600">{error}</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-xl text-gray-600">
              {searchTerm
                ? t("scholarships.page.noResults", "No scholarships match your search.")
                : t("scholarships.page.empty", "No scholarships available yet. Please check back soon.")}
            </p>
          </div>
        ) : (
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {filtered.map((scholarship, index) => (
              <ScholarshipCard
                key={scholarship.id}
                scholarship={scholarship}
                index={index}
                t={t}
                onContactClick={() => setShowContactPopup(true)}
              />
            ))}
          </motion.div>
        )}
      </div>

      <Footer />
      <ContactPopup isOpen={showContactPopup} onClose={() => setShowContactPopup(false)} />
    </div>
  );
};

export default EventsPage;
